import { useI18n } from "@/lib/i18n";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Database,
  LayoutDashboard,
  ShieldAlert,
  Layers,
  MousePointerClick,
  Info,
  Phone,
  Sparkles,
  Check,
} from "lucide-react";
import NewsletterForm from "@/components/NewsletterForm";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const Home = () => {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const base = `/${lang}`;

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ block: "start" });
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden bg-slate-950 text-white"
      >
        <div className="absolute inset-0 grid-pattern-dark opacity-40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div
                data-testid="hero-badge"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-xs font-mono uppercase tracking-widest mb-6"
              >
                <Sparkles className="w-3 h-3" />
                {t("hero.badge")}
              </div>
              <h1
                data-testid="hero-headline"
                className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-6"
              >
                {t("hero.title_line1")}{" "}
                <span className="text-emerald-400">{t("hero.title_line2")}</span>
              </h1>
              <p className="text-xl sm:text-2xl text-slate-200 font-medium mb-6">
                {t("hero.subtitle")}
              </p>
              <p
                data-testid="hero-subtext"
                className="text-slate-300 text-base sm:text-lg leading-relaxed max-w-2xl mb-8"
              >
                {t("hero.description")}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => scrollTo("lead-form")}
                  data-testid="hero-cta-button"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md bg-emerald-500 text-slate-900 font-semibold hover:bg-emerald-400"
                >
                  {t("hero.cta_primary")}
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => scrollTo("how")}
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md border border-slate-700 text-slate-100 hover:bg-slate-900"
                >
                  {t("hero.cta_secondary")}
                </button>
              </div>

              <div className="mt-12 grid grid-cols-3 gap-6 max-w-xl">
                <div>
                  <div className="eyebrow eyebrow-dark mb-2">{t("hero.trust_label")}</div>
                  <div className="text-sm text-white font-semibold">{t("hero.trust_value")}</div>
                </div>
                <div>
                  <div className="eyebrow eyebrow-dark mb-2">{t("hero.coverage_label")}</div>
                  <div className="text-sm text-white font-semibold">{t("hero.coverage_value")}</div>
                </div>
                <div>
                  <div className="eyebrow eyebrow-dark mb-2">{t("hero.focus_label")}</div>
                  <div className="text-sm text-white font-semibold">{t("hero.focus_value")}</div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden border border-slate-800 shadow-2xl">
                <img
                  src="/images/hero.jpg"
                  alt={t("hero.image_alt")}
                  data-testid="hero-image"
                  className="w-full h-[420px] object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-slate-950/80 via-slate-950/30 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 bg-slate-900/85 backdrop-blur border border-slate-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-mono text-[10px] text-emerald-400 uppercase tracking-widest">
                      Ecuasuelo · Panel
                    </span>
                    <span className="font-mono text-[10px] text-slate-400">
                      2026
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { l: t("insight.kpi1_label"), v: t("insight.kpi1_value") },
                      { l: t("insight.kpi2_label"), v: t("insight.kpi2_value") },
                      { l: t("insight.kpi3_label"), v: t("insight.kpi3_value") },
                    ].map((k, i) => (
                      <div key={i} className="rounded-md bg-slate-800/70 px-3 py-2 border border-slate-700">
                        <div className="text-[9px] font-mono uppercase tracking-widest text-slate-400 mb-1">
                          {k.l}
                        </div>
                        <div className="text-xs font-semibold text-white truncate">{k.v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" data-testid="about-section" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="eyebrow mb-4">{t("about.eyebrow")}</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 leading-tight">
                {t("about.title")}
              </h2>
              <p className="text-lg text-slate-600 mb-6">{t("about.subtitle")}</p>
              <p className="text-slate-600 leading-relaxed mb-4">{t("about.paragraph1")}</p>
              <p className="text-slate-600 leading-relaxed">{t("about.paragraph2")}</p>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:row-span-2 rounded-xl overflow-hidden border border-slate-200">
                  <img
                    src="/images/meeting.jpg"
                    alt={t("about.image_alt")}
                    className="w-full h-full object-cover min-h-[280px]"
                  />
                </div>
                {[
                  { icon: Database, k: "card1" },
                  { icon: Layers, k: "card2" },
                  { icon: Info, k: "card3" },
                ].slice(0, 2).map((c, i) => {
                  const Icon = c.icon;
                  return (
                    <div key={i} className="rounded-xl p-5 border border-slate-200 bg-slate-50">
                      <div className="w-9 h-9 rounded-md bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mb-3">
                        <Icon className="w-4 h-4 text-emerald-600" strokeWidth={2.2} />
                      </div>
                      <h3 className="font-display font-semibold text-slate-900 mb-1">
                        {t(`about.${c.k}_title`)}
                      </h3>
                      <p className="text-sm text-slate-600 leading-relaxed">
                        {t(`about.${c.k}_desc`)}
                      </p>
                    </div>
                  );
                })}
                <div className="sm:col-span-2 rounded-xl p-5 border border-slate-200 bg-slate-50">
                  <div className="w-9 h-9 rounded-md bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mb-3">
                    <Info className="w-4 h-4 text-emerald-600" strokeWidth={2.2} />
                  </div>
                  <h3 className="font-display font-semibold text-slate-900 mb-1">
                    {t("about.card3_title")}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {t("about.card3_desc")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" data-testid="how-it-works-section" className="py-24 bg-slate-50 relative">
        <div className="absolute inset-0 grid-pattern opacity-70 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-12">
            <div className="eyebrow mb-4">{t("how.eyebrow")}</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 leading-tight">
              {t("how.title")}
            </h2>
            <p className="text-lg text-slate-600 mb-4">{t("how.subtitle")}</p>
            <p className="text-slate-600 leading-relaxed">{t("how.description")}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {["step1", "step2", "step3", "step4"].map((s, i) => (
              <div
                key={s}
                data-testid={`how-step-${i + 1}`}
                className="relative rounded-xl bg-white border border-slate-200 p-6 hover:border-emerald-500"
              >
                <div className="font-mono text-xs text-emerald-600 tracking-widest mb-3">
                  {t(`how.${s}_number`)}
                </div>
                <h3 className="font-display text-lg font-semibold text-slate-900 mb-2">
                  {t(`how.${s}_title`)}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {t(`how.${s}_desc`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLARITY (split image) */}
      <section data-testid="clarity-section" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-2xl overflow-hidden border border-slate-200 order-2 lg:order-1">
            <img
              src="/images/dashboard.jpg"
              alt={t("clarity.image_alt")}
              className="w-full h-[420px] object-cover"
            />
          </div>
          <div className="order-1 lg:order-2">
            <div className="eyebrow mb-4">{t("clarity.eyebrow")}</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
              {t("clarity.title")}
            </h2>
            <p className="text-lg text-slate-600 mb-6">{t("clarity.subtitle")}</p>
            <ul className="space-y-3">
              {["bullet1", "bullet2", "bullet3", "bullet4"].map((b) => (
                <li key={b} className="flex gap-3">
                  <span className="mt-1 shrink-0 w-5 h-5 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
                    <Check className="w-3 h-3 text-emerald-600" strokeWidth={3} />
                  </span>
                  <span className="text-slate-600 leading-relaxed">{t(`clarity.${b}`)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ADVANTAGES */}
      <section id="advantages" data-testid="advantages-section" className="py-24 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern-dark opacity-40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-14">
            <div className="eyebrow eyebrow-dark mb-4">{t("advantages.eyebrow")}</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
              {t("advantages.title")}
            </h2>
            <p className="text-lg text-slate-300 mb-4">{t("advantages.subtitle")}</p>
            <p className="text-slate-400 leading-relaxed">{t("advantages.paragraph")}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: MousePointerClick, k: "item1" },
              { icon: LayoutDashboard, k: "item2" },
              { icon: ShieldAlert, k: "item3" },
              { icon: Phone, k: "item4" },
            ].map(({ icon: Icon, k }) => (
              <div
                key={k}
                data-testid={`advantage-${k}`}
                className="p-6 rounded-xl border border-slate-800 bg-slate-950/60 hover:border-emerald-500"
              >
                <div className="w-10 h-10 rounded-md bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-emerald-400" strokeWidth={2} />
                </div>
                <h3 className="font-display font-semibold mb-2">{t(`advantages.${k}_title`)}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{t(`advantages.${k}_desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INSIGHT / COMPLEXITY */}
      <section data-testid="insight-section" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <div>
            <div className="eyebrow mb-4">{t("insight.eyebrow")}</div>
            <h2 className="font-display text-3xl font-bold text-slate-900 mb-3">
              {t("insight.title")}
            </h2>
            <p className="text-lg text-slate-600 mb-4">{t("insight.subtitle")}</p>
            <p className="text-slate-600 leading-relaxed mb-6">{t("insight.text")}</p>
            <div className="grid grid-cols-3 gap-3">
              {["kpi1", "kpi2", "kpi3"].map((k) => (
                <div key={k} className="rounded-lg border border-slate-200 p-4">
                  <div className="eyebrow mb-1">{t(`insight.${k}_label`)}</div>
                  <div className="text-sm font-semibold text-slate-900">
                    {t(`insight.${k}_value`)}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="eyebrow mb-4">{t("complexity.eyebrow")}</div>
            <h2 className="font-display text-3xl font-bold text-slate-900 mb-3">
              {t("complexity.title")}
            </h2>
            <p className="text-lg text-slate-600 mb-6">{t("complexity.subtitle")}</p>
            <ul className="space-y-3">
              {["bullet1", "bullet2", "bullet3", "bullet4"].map((b) => (
                <li key={b} className="flex gap-3">
                  <span className="mt-1 shrink-0 w-5 h-5 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
                    <Check className="w-3 h-3 text-emerald-600" strokeWidth={3} />
                  </span>
                  <span className="text-slate-600 leading-relaxed">
                    {t(`complexity.${b}`)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* STRUCTURED */}
      <section data-testid="structured-section" className="py-24 bg-slate-50 relative">
        <div className="absolute inset-0 grid-pattern opacity-70 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="eyebrow mb-4">{t("structured.eyebrow")}</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
              {t("structured.title")}
            </h2>
            <p className="text-lg text-slate-600 mb-6">{t("structured.subtitle")}</p>
            <ul className="space-y-3">
              {["bullet1", "bullet2", "bullet3", "bullet4"].map((b) => (
                <li key={b} className="flex gap-3">
                  <span className="mt-1 shrink-0 w-5 h-5 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
                    <Check className="w-3 h-3 text-emerald-600" strokeWidth={3} />
                  </span>
                  <span className="text-slate-600 leading-relaxed">
                    {t(`structured.${b}`)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-lg">
            <img
              src="/images/analytics.jpg"
              alt={t("about.image_alt")}
              className="w-full h-[440px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* NEWSLETTER FORM */}
      <NewsletterForm />

      {/* CLOSING */}
      <section data-testid="closing-section" className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="eyebrow mb-4">{t("closing.eyebrow")}</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            {t("closing.title")}
          </h2>
          <p className="text-lg text-slate-600 mb-6">{t("closing.subtitle")}</p>
          <p className="text-slate-600 leading-relaxed mb-4">{t("closing.paragraph1")}</p>
          <p className="text-slate-600 leading-relaxed">{t("closing.paragraph2")}</p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" data-testid="faq-section" className="py-24 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <div className="eyebrow mb-4">{t("faq.eyebrow")}</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-3">
              {t("faq.title")}
            </h2>
            <p className="text-lg text-slate-600">{t("faq.subtitle")}</p>
          </div>

          <Accordion type="single" collapsible data-testid="faq-accordion" className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-200">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <AccordionItem key={n} value={`item-${n}`} className="px-6">
                <AccordionTrigger
                  data-testid={`faq-question-${n}`}
                  className="text-left font-display text-base font-semibold text-slate-900 hover:text-emerald-600"
                >
                  {t(`faq.q${n}`)}
                </AccordionTrigger>
                <AccordionContent
                  data-testid={`faq-answer-${n}`}
                  className="text-slate-600 leading-relaxed pb-5"
                >
                  {t(`faq.a${n}`)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default Home;
